import { useState, useEffect, useMemo, useCallback } from 'react';
import { eventService } from '../services/eventService';
import dayjs from 'dayjs';

/**
 * Laravel casts `start_time` / `end_time` as datetimes, so the API often returns full ISO strings.
 * Parsing with `split(':')` breaks those values and yields invalid dates on the calendar.
 */
function combineLocalDateTime(dateField, timeField) {
  const d = dayjs(dateField);
  if (!d.isValid()) return null;
  if (timeField == null || timeField === '') {
    return d.startOf('day').toDate();
  }
  const s = String(timeField);
  if (s.includes('T') || /^\d{4}-\d{2}-\d{2}[ T]/.test(s)) {
    const t = dayjs(timeField);
    if (t.isValid()) {
      return d.hour(t.hour()).minute(t.minute()).second(t.second()).millisecond(0).toDate();
    }
  }
  const parts = s.split(':');
  const h = parseInt(parts[0], 10);
  const m = parseInt(String(parts[1] ?? '0').replace(/\D.*/, ''), 10);
  if (!Number.isFinite(h)) return d.startOf('day').toDate();
  return d.hour(h).minute(Number.isFinite(m) ? m : 0).second(0).millisecond(0).toDate();
}

// Match EventList TYPE_NEON — solid fills use readable contrast with white labels
const NEON = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';
// Distinct colors per phase so periods are easy to tell apart; `familyAccent` ties them to the same parent event.
const PHASES = [
  { key: 'design', label: 'Design', start: 'design_start_date', end: 'design_end_date', color: '#38BDF8' },
  { key: 'mechanism', label: 'Mechanism', start: 'mechanism_start_date', end: 'mechanism_end_date', color: '#818CF8' },
  { key: 'electronic', label: 'Electronic', start: 'electronic_start_date', end: 'electronic_end_date', color: '#FBBF24' },
  { key: 'programming', label: 'Programming', start: 'programming_start_date', end: 'programming_end_date', color: '#34D399' },
  { key: 'tuning', label: 'Tuning', start: 'tuning_start_date', end: 'tuning_end_date', color: '#F87171' },
  { key: 'demo', label: 'Demo Day', start: 'demo_day', end: 'demo_day', time: 'demo_time', color: '#F59E0B' },
];

export const useCalendarEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getEventColor = useCallback((type) => {
    const colors = {
      Demo: NEON,
      Prototype: NEON2,
      Training: NEON3,
      LabCleaning: '#F59E0B',
      Other: '#64748b',
    };
    return colors[type] || colors.Other;
  }, []);

  const getEventTextColor = useCallback(() => '#ffffff', []);

  const parseDateTime = useCallback((dateVal, timeVal) => {
    if (!dateVal) return null;
    const baseDate = dayjs(dateVal);
    if (!baseDate.isValid()) return null;

    if (!timeVal) return baseDate.toDate();

    const [hour, minute] = String(timeVal).split(':');
    return baseDate.hour(parseInt(hour, 10)).minute(parseInt(minute, 10)).toDate();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await eventService.getAll();
      setEvents(data);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const calendarEvents = useMemo(() => {
    return events.flatMap((event) => {
      const startDate = dayjs(event.start_date);
      const endDate = dayjs(event.end_date);

      const start = combineLocalDateTime(event.start_date, event.start_time);
      let end;
      if (event.end_time) {
        end = combineLocalDateTime(event.end_date, event.end_time);
      } else if (event.start_time) {
        end = startDate.hour(23).minute(59).second(0).millisecond(0).toDate();
      } else {
        end = endDate.toDate();
      }

      if (!start || !end) {
        return [];
      }

      const baseColor = getEventColor(event.type);
      const baseEvent = {
        id: event.id,
        title: event.name,
        start,
        end,
        allDay: !event.start_time,
        resource: {
          ...event,
          color: baseColor,
          textColor: getEventTextColor(event.type),
          depth: 0,
          phase: null,
          parentEventId: event.id,
          isPhase: false,
          familyAccent: baseColor,
        },
      };

      if (!['Demo', 'Prototype'].includes(event.type) || !event.training_details) {
        return [baseEvent];
      }

      const detail = event.training_details;
      const phaseEvents = PHASES.map((phase) => {
        const phaseStart = parseDateTime(detail[phase.start], phase.time ? detail[phase.time] : null);
        if (!phaseStart) return null;

        const phaseEnd = phase.time
          ? dayjs(phaseStart).add(1, 'hour').toDate()
          : parseDateTime(detail[phase.end], null) || phaseStart;
        if (!phaseEnd) return null;

        return {
          id: `${event.id}-${phase.key}`,
          title: `${event.name} · ${phase.label}`,
          start: phaseStart,
          end: phaseEnd,
          allDay: false,
          resource: {
            ...event,
            color: phase.color,
            textColor: '#ffffff',
            depth: 0,
            phase: phase.label,
            parentEventId: event.id,
            isPhase: true,
            familyAccent: baseColor,
          },
        };
      }).filter(Boolean);

      return phaseEvents.length > 0 ? phaseEvents : [baseEvent];
    });
  }, [events, getEventColor, getEventTextColor, parseDateTime]);

  const filterEventsByType = (types) => {
    if (!types || types.length === 0) return calendarEvents;
    return calendarEvents.filter(event => types.includes(event.resource.type));
  };

  const filterEventsByStatus = (statuses) => {
    if (!statuses || statuses.length === 0) return calendarEvents;
    return calendarEvents.filter(event => statuses.includes(event.resource.status));
  };

  const getEventsInRange = (startDate, endDate) => {
    return calendarEvents.filter(event => {
      const eventStart = dayjs(event.start);
      const eventEnd = dayjs(event.end);
      const rangeStart = dayjs(startDate);
      const rangeEnd = dayjs(endDate);

      return eventStart.isBefore(rangeEnd) && eventEnd.isAfter(rangeStart);
    });
  };

  const getUpcomingEvents = (days = 7) => {
    const now = dayjs();
    const futureDate = now.add(days, 'day');

    return calendarEvents
      .filter(event => {
        const eventStart = dayjs(event.start);
        return eventStart.isAfter(now) && eventStart.isBefore(futureDate);
      })
      .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));
  };

  return {
    events,
    calendarEvents,
    loading,
    error,
    setError,
    fetchEvents,
    filterEventsByType,
    filterEventsByStatus,
    getEventsInRange,
    getUpcomingEvents,
    getEventColor,
    getEventTextColor,
  };
};
